import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from "@angular/core";
import { LogService } from "ng/src/app/logs/log.service";
import { ChoiceListService } from "ng/src/app/choice-lists/shared/choice-list.service";
import { TagService } from "ng/src/app/tags/tag.service";
import { Template, TemplateCopyOverride, TemplateSegment, TemplateSegmentTypeId, TemplateTypeId } from "ng/src/app/templates/shared/template.model";
import { Log, LogRoleId } from "ng/src/app/logs/log.model";
import { BehaviorSubject, merge, Observable, of, Subject } from "rxjs";
import { defaultIfEmpty, filter, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { TemplateService } from "ng/src/app/templates/shared/template.service";
import { Utils } from "ng/src/app/core/utils";
import { ConfirmationService, PrimeTemplate } from "primeng/api";
import { ChoiceList, ChoiceListItem } from "ng/src/app/choice-lists/shared/choice-list.model";
import { TagValue } from "ng/src/app/tags/tag.model";
import { HelpDialogItem } from "ng/src/app/core/help-dialog/help-dialog.model";
import { HelpDialogService } from "ng/src/app/core/help-dialog/help-dialog.service";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { HelpSection } from "ng/src/app/core/help-section";

@Component({
	selector: 'lb-template-copy[template]',
	templateUrl: 'template-copier.component.html',
	styleUrls: ['template-copier.component.scss']
})
export class TemplateCopierComponent implements OnInit, OnDestroy {
	
	@ViewChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;
	
	@Input()
	get template() {
		return this.templateSubject.value;
	}
	set template(val) {
		this.templateSubject.next(val);
	}
	
	@Input()
	get visible() {
		return this._visible;
	}
	set visible(val) {
		this._visible = val;
		this.visibleChange.next(val);
	}
	
	
	@Output()
	complete = new EventEmitter<number>();
	
	@Output()
	visibleChange = new EventEmitter<boolean>();
	
	segments = new Array<TemplateSegment>();
	segmentsNeedingOverride: { [segmentId: number]: true } = {};
	noSegmentsNeedingOverride = false;
	typeNames: Record<TemplateSegmentTypeId, string> = {} as any;
	
	private _selectedLog?: [Log];
	get selectedLog() {
		return this._selectedLog;
	}
	set selectedLog(val) {
		this._selectedLog = val;
		this.logSubject.next(this.valueFromArray(val));
	}
	
	get selectedLogId() {
		return this.valueFromArray(this.selectedLog)?.id;
	}
	
	loadingOverrides = false;
	loadingSegments = false;
	saving = false;
	
	overrides: {[segmentId: number]: Overrides} = {};
	simpleOverrides = new Array<SimpleOverride>();
	
	hasErrors = false;
	
	newTemplateId?: number;
	
	readonly LOG_ROLES = LogRoleId;
	readonly SEGMENT_ID = TemplateSegmentTypeId;
	readonly AppStateService = AppStateService;
	
	private _visible = false;
	private templateSubject = new BehaviorSubject<Template | null>(null);
	private logSubject = new BehaviorSubject<Log | null>(null);
	private onDestroy$ = new Subject();
	
	get templateIsInspection() {
		return this.template?.templateType?.id === TemplateTypeId.INSPECTION;
	}
	
	get sameLog() {
		return this.valueFromArray(this.selectedLog)?.id === this.template?.logId;
	}
	
	get currentLog() {
		return LogService.selectedLog;
	}
	
	constructor(
		private logService: LogService,
		private choiceListService: ChoiceListService,
		private tagService: TagService,
		private templateService: TemplateService,
		private confirmService: ConfirmationService,
		private helpDialogService: HelpDialogService
	) {}
	
	ngOnInit(): void {
		const templatePipe$ = this.templateSubject.pipe(
			takeUntil(this.onDestroy$),
			filter(template => template != null),
			tap(() => {
				this.loadingSegments = true;
			}),
			switchMap(template => {
				if (this.sameLog)
					return of([]).pipe(defaultIfEmpty([]));
				return this.templateService.fetchTemplateSegments(template!.id);
			}),
			map((segments) => segments.filter(segment => !segment.obsolete)),
			tap((segments) => this.segments = segments),
			tap(() => this.loadingSegments = false)
		);
		
		merge(
			templatePipe$,
			this.logSubject
		)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.loadingOverrides = true),
				switchMap(() => this.resetOverrides())
			)
			.subscribe(() => {
				this.loadingOverrides = false;
			})
		
		this.templateService.getTemplateSegmentTypes()
			.subscribe(types => {
				this.typeNames = Utils.arrayToMap( types._embedded.templateSegmentTypes, type => {
					let name = type.name;
					if (type.id === TemplateSegmentTypeId.SUB_TEMPLATE) {
						name = this.templateIsInspection ? 'Section' : 'Sub Template';
					}
					return [ type.id, name ];
				}) as any;
			});
	}
	
	ngOnDestroy(): void {
		this.onDestroy$.next();
	}
	
	valueFromArray<T>(arr?: T | T[]): T | null {
		return (Array.isArray(arr) ? arr[0] : arr) ?? null;
	}
	proceed() {
		let copy$: Observable<Record<'id', number>>;
		
		if (this.sameLog) {
			copy$ = this.templateService.copyTemplate(this.template!.id);
		}
		else {
			const overrides: Record<number, TemplateCopyOverride> = {};
			
			const hasErrors = this.segments.some(segment => {
				if (!this.segmentsNeedingOverride[segment.id])
					return false;
				
				const override: TemplateCopyOverride = {};
				let issue = false;
				
				switch (segment.type.id) {
					case TemplateSegmentTypeId.TAG_CHOICE:
					case TemplateSegmentTypeId.TAG:
						override.tagValue = this.valueFromArray(this.overrides[segment.id].tagValue)?.id ?? null;
						
						if (segment.type.id === TemplateSegmentTypeId.TAG)
							issue = override.tagValue == null;
						break;
					
					case TemplateSegmentTypeId.TEXT_CHOICE:
						const val = this.overrides[segment.id].choiceList;
						override.choiceList = typeof val === 'string' ? val : this.valueFromArray(val)?.id ?? null;
						override.defaultChoice = this.valueFromArray(this.overrides[segment.id].defaultChoice)?.id ?? null;
						issue = override.choiceList == null;
						break;
				}
				overrides[segment.id] = override;
				return issue;
			});
			
			if ( (this.hasErrors = hasErrors) )
				return;
			
			copy$ = this.templateService.copyTemplateToLog(this.template!.id, this.valueFromArray(this.selectedLog)!.id, overrides);
		}
		
		this.saving = true;
		copy$.subscribe((value) => {
			this.complete.emit(value.id);
			this.newTemplateId = value.id;
		})
			.add(() => this.saving = false);
	}
	
	openNewTemplate() {
		window.open(`/#/log-detail?log=${this.selectedLogId}&tab=templates&editTemplate=${this.newTemplateId}`, '_blank');
	}
	
	setToOthers(overrides: Overrides, replacing: {type: OverrideableTypes, id: number}) {
		const segmentMap = Utils.arrayToMap(this.segments, s => s.id);
		
		switch (replacing.type) {
			case TemplateSegmentTypeId.TAG:
			case TemplateSegmentTypeId.TAG_CHOICE:
				Object.entries(this.overrides).forEach(([segmentId, override]) => {
					const segment = segmentMap[segmentId];
					if (segment.tagValue?.id === replacing.id) {
						override.tagValue = overrides.tagValue;
					}
				});
				break;
				
			case TemplateSegmentTypeId.TEXT_CHOICE:
				Object.entries(this.overrides).forEach(([segmentId, override]) => {
					const segment = segmentMap[segmentId];
					if (segment.choiceList?.id === replacing.id) {
						override.choiceList = overrides.choiceList;
						override.defaultChoice = overrides.defaultChoice;
					}
				});
				break;
		}
	}
	
	private resetOverrides() {
		delete this.newTemplateId;
		this.hasErrors = false;
		this.segmentsNeedingOverride = {};
		this.overrides = {};
		this.simpleOverrides = [];
		
		let needsOverride = this.segments.filter(segment => {
			return [
				TemplateSegmentTypeId.TAG,
				TemplateSegmentTypeId.TAG_CHOICE,
				TemplateSegmentTypeId.TEXT_CHOICE
			].some(id => id === segment.type.id);
		});
		
		this.noSegmentsNeedingOverride = needsOverride.length === 0;
		
		const simpleOverridesMap: Record<string, SimpleOverride> = {};
		
		needsOverride.forEach((segment) => {
			this.overrides[segment.id] = new Overrides();
			
			this.segmentsNeedingOverride[segment.id] = true;
			
			switch (segment.type.id) {
				case TemplateSegmentTypeId.TAG:
				case TemplateSegmentTypeId.TAG_CHOICE:
					if (!segment.tagValue) return;
					setIntoMap(segment.type.id, segment.tagValue.id, segment.tagValue.viewString);
					break;
					
				case TemplateSegmentTypeId.TEXT_CHOICE:
					if (!segment.choiceList) return;
					setIntoMap(segment.type.id, segment.choiceList.id, segment.choiceList.name);
			}
		});
		
		this.simpleOverrides = Object.values(simpleOverridesMap);
		
		if (!this.selectedLogId || this.simpleOverrides.length == 0)
			return of({});
		
		return this.checkChoiceListCopyStatus(this.selectedLogId, this.simpleOverrides.filter(o => o.type === TemplateSegmentTypeId.TEXT_CHOICE).map(o => ({ id: o.id, name: o.name })))
			.pipe(tap(res => {
				const overridesWithList = Utils.collectToMap(this.segments.filter(s => !!s.choiceList), s => [s.choiceList!.id, this.overrides[s.id]]);
				
				this.simpleOverrides.forEach(o => {
					const result = res[o.id];
					
					if (result === true)
						o.choiceListCopyStatus = 'disabled'
					else if (typeof result === 'number') {
						o.choiceListCopyStatus = 'hide';
						o.choiceList = [{ id: result, name: o.name }];
						this.setToOthers(o, o);
					}
					else
						o.choiceListCopyStatus = 'show';
					
					overridesWithList[o.id]?.forEach(ov => ov.choiceListCopyStatus = o.choiceListCopyStatus);
				});
			}));
		
		function setIntoMap(type: OverrideableTypes, id: number, name: string) {
			const mapKey = `${type},${id}`;
			if (mapKey in simpleOverridesMap) return;
			simpleOverridesMap[id] = new SimpleOverride(type, id, name);
		}
	}
	
	private checkChoiceListCopyStatus(logId: number, lists: Pick<ChoiceList, 'id' | 'name'>[]) {
		const listMap = Utils.arrayToMap(lists, l => [l.id.toString(), l]);
		
		return this.choiceListService.fetchExistInLog(lists.map(l => l.id), logId).pipe(
			switchMap(resMap => {
				const namesToCheck = Object.entries(listMap)
					.filter(entry => !resMap[entry[0]])
					.map(entry => entry[1].name);
				
				const ob$: Observable<Record<string, boolean>> = namesToCheck.length == 0
					? of({})
					: this.choiceListService.fetchLists({ log: logId, name: lists.map(l => l.name) })
						.pipe(map(res => {
							const nameUseMap = Utils.arrayToMap(res, r => r.name);
							return Utils.arrayToMap(lists, l => [l.name, l.name in nameUseMap]);
						}));
				
				return ob$.pipe(
					map(nameResMap => {
						return Utils.arrayToMap(
							Object.entries(listMap), entry => {
								const id = entry[0];
								let val: boolean | number = false;
								if (nameResMap[entry[1].name]) {
									val = true;
								}
								else if (resMap[id]) {
									val = resMap[id]!;
								}
								return [id, val];
							});
					})
				);
			})
		);
	}
	
	showHelpDialog(index?: number) {
		const templateMap = Utils.arrayToMap(Array.from(this.templates), pT => [pT.name, pT.template]);
		
		this.helpDialogService.next({
			items: [
				HelpDialogItem.of(templateMap['helpDialogIntro'])
					.setHeader('Template Copy Help')
					.setDetailLink({text: 'Click for more help', link: HelpSection.getHelpLink('copying-templates')}),
				HelpDialogItem.of(templateMap['helpDialogLogSelect'])
					.setHeader('Target Log Selection')
					.setPictureUrl('/assets/images/help/template-copier/log-select.png')
					.setDetailLink({text: 'Click for more help', link: HelpSection.getHelpLink('copying-templates')}),
				HelpDialogItem.of(templateMap['helpDialogListSelect'])
					.setHeader('List Mapping')
					.setPictureUrl('/assets/images/help/template-copier/list-select.png')
					.setDetailLink({text: 'Click for more help', link: HelpSection.getHelpLink('copying-templates')}),
				HelpDialogItem.of(templateMap['helpDialogTagSelect'])
					.setHeader('Tag Mapping')
					.setPictureUrl('/assets/images/help/template-copier/tag-select.png')
					.setDetailLink({text: 'Click for more help', link: HelpSection.getHelpLink('copying-templates')}),
				HelpDialogItem.of(templateMap['helpDialogIncomplete'])
					.setHeader('Incomplete Notice')
					.setPictureUrl('/assets/images/help/template-copier/error-message.png')
					.setDetailLink({text: 'Click for more help', link: HelpSection.getHelpLink('copying-templates')})
			],
			index });
	}
}

class Overrides {
	choiceList?: [Pick<ChoiceList, 'id' | 'name'>] | 'COPY' | null;
	tagValue?: [TagValue] | null;
	defaultChoice?: [ChoiceListItem] | null;
	
	choiceListCopyStatus: 'show' | 'hide' | 'disabled' = 'disabled';
	
	get choiceListCopyEnabled() {
		return this.choiceList === 'COPY';
	}
	set choiceListCopyEnabled(val) {
		this.choiceList = val ? 'COPY' : null;
	}
}

class SimpleOverride extends Overrides {
	
	constructor(
		public type: OverrideableTypes,
		public id: number,
		public name: string
	) {
		super();
	}
}

type OverrideableTypes = TemplateSegmentTypeId.TAG | TemplateSegmentTypeId.TAG_CHOICE | TemplateSegmentTypeId.TEXT_CHOICE;